/**
 * /customer/cards — full grid of joined loyalty cards (Phase 2b).
 *
 * Mirrors mobile/(customer)/cards.tsx interaction language. The
 * "My Cards" bottom-nav now lands here directly (rather than the
 * old `/customer/home#cards` anchor). The whole card tile is a
 * `<Link>` to the per-card detail surface — no extra buttons,
 * matching the operator's D2=A decision.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Coffee, ChevronRight } from 'lucide-react';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';

export default function CustomerCardsPage() {
  const token = useAuthStore((s) => s.token);

  const cardsQ = useQuery({
    queryKey: qk.customerCards(),
    queryFn: () => api.customerCards(token),
    enabled: !!token,
  });

  return (
    <div className="space-y-6" data-testid="customer-cards-screen">
      <div>
        <div className="text-xs uppercase tracking-widest text-gold font-mono mb-2">
          Loyalty
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold">My Cards</h1>
        <p className="text-sm text-white/55 mt-1.5">
          Every brand you collect stamps with, in one place.
        </p>
      </div>

      {cardsQ.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-44 rounded-3xl bg-white/[0.03] border border-white/5 animate-pulse"
            />
          ))}
        </div>
      ) : (cardsQ.data ?? []).length === 0 ? (
        <div
          className="rounded-3xl border border-white/8 bg-[#15151B] p-10 text-center"
          data-testid="customer-cards-empty-state"
        >
          <Coffee size={28} className="mx-auto text-gold/60 mb-3" />
          <div className="text-base font-medium">No cards yet</div>
          <div className="text-xs text-white/45 mt-1 mb-5">
            Join a brand to start collecting stamps.
          </div>
          <Link
            to="/customer/brands"
            data-testid="customer-cards-explore-brands"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gold/15 border border-gold/45 text-gold text-sm font-medium hover:bg-gold/20 transition-colors"
          >
            Explore brands <ChevronRight size={14} />
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cardsQ.data.map((card) => {
            const next = card.next_reward_at ?? card.brand?.stamps_per_card ?? 10;
            const cur = card.current_stamps ?? 0;
            const pct = Math.min(100, (cur / Math.max(1, next)) * 100);
            const tint = card.brand?.primary_color || '#C9A84C';
            return (
              <Link
                key={card.id}
                to={`/customer/cards/${card.id}`}
                data-testid={`customer-cards-card-${card.id}`}
                className="group rounded-3xl bg-[#15151B] border border-white/8 hover:border-gold/35 hover:shadow-glow-gold-sm transition-all overflow-hidden"
              >
                {/* Banner band — banner image if present, otherwise the
                    brand's primary_color washes across as a clean
                    branded fill. */}
                <div
                  className="h-24 relative"
                  style={
                    card.brand?.banner_url
                      ? {
                          backgroundImage: `url(${resolveImageUrl(card.brand.banner_url)})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }
                      : { background: `linear-gradient(135deg, ${tint}40 0%, #000 100%)` }
                  }
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  {card.brand?.logo_url ? (
                    <img
                      src={resolveImageUrl(card.brand.logo_url)}
                      alt=""
                      className="absolute bottom-3 left-3 w-12 h-12 rounded-2xl border border-white/15 shadow-xl bg-black/40"
                    />
                  ) : null}
                </div>
                <div className="px-4 py-4">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="font-semibold text-sm truncate">
                      {card.brand?.name}
                    </div>
                    <div
                      className="text-xs font-mono shrink-0"
                      style={{ color: tint }}
                    >
                      {cur} / {next}
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: tint,
                      }}
                    />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
